/* Heirloom Contact — guestbook page. Paper-cream background with
   caramel hairlines. Italic labels. Album-credits feel. */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function HeirloomContact() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { document.title = `${studioName} — Guestbook`; }, [studioName]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please add your name, email, and a short message."); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Your entry has been added to the guestbook.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not deliver — please try again.");
    } finally { setSending(false); }
  }

  return (
    <div data-testid="heirloom-contact" style={{ background: "var(--tenant-bg, #F8F2EA)", color: "var(--tenant-fg, #2E2419)" }}>
      <section className="px-6 lg:px-16 pt-20 pb-32">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Left: heading + studio address page */}
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: "var(--tenant-accent)" }}>
              The Guestbook
            </div>
            <h1 className="italic mb-8" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)" }}>
              Sign your name.
            </h1>
            <p className="italic text-base leading-loose mb-10" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft)" }}>
              Pen us a note. Whether you are planning a sitting or just admiring the bindings, we read every entry.
            </p>
            <div className="space-y-4 text-sm pt-6 border-t" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)", borderStyle: "dotted" }}>
              {settings?.contact_location && (
                <div className="flex items-start gap-3 italic" style={{ fontFamily: "var(--tenant-serif)" }}>
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tenant-accent)" }} strokeWidth={1.25} />
                  {settings.contact_location}
                </div>
              )}
              {settings?.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="flex items-start gap-3 italic underline-offset-4 hover:underline" style={{ fontFamily: "var(--tenant-serif)" }}>
                  <Mail className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tenant-accent)" }} strokeWidth={1.25} />
                  {settings.contact_email}
                </a>
              )}
              {settings?.contact_phone && (
                <a href={`tel:${settings.contact_phone}`} className="flex items-start gap-3 italic underline-offset-4 hover:underline" style={{ fontFamily: "var(--tenant-serif)" }}>
                  <Phone className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tenant-accent)" }} strokeWidth={1.25} />
                  {settings.contact_phone}
                </a>
              )}
              {settings?.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 italic underline-offset-4 hover:underline" style={{ fontFamily: "var(--tenant-serif)" }}>
                  <Instagram className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tenant-accent)" }} strokeWidth={1.25} />
                  Instagram
                </a>
              )}
            </div>
          </div>

          {/* Right: guestbook form */}
          <form onSubmit={onSubmit} className="md:col-span-7 space-y-6">
            {[
              { name: "name", label: "Your name" },
              { name: "email", label: "Email" },
              { name: "phone", label: "Telephone (optional)" },
            ].map((f) => (
              <div key={f.name}>
                <label className="block italic text-sm mb-2" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}>
                  {f.label}
                </label>
                <input type={f.name === "email" ? "email" : f.name === "phone" ? "tel" : "text"}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  data-testid={`heirloom-contact-${f.name}`}
                  className="w-full bg-transparent border-b-2 py-2 outline-none focus:border-current italic"
                  style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 50%, transparent)", fontFamily: "var(--tenant-serif)" }} />
              </div>
            ))}
            <div>
              <label className="block italic text-sm mb-2" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}>Your entry</label>
              <textarea rows={6} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="heirloom-contact-message"
                className="w-full bg-transparent border-b-2 py-2 outline-none focus:border-current italic resize-none"
                style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 50%, transparent)", fontFamily: "var(--tenant-serif)" }} />
            </div>
            <button type="submit" disabled={sending} data-testid="heirloom-contact-submit"
              className="inline-flex items-center gap-3 px-8 py-3 text-[10px] uppercase tracking-[0.42em] border-2 disabled:opacity-60"
              style={{ borderColor: "var(--tenant-fg)", color: "var(--tenant-fg)" }}>
              {sending ? "Binding..." : "Sign the guestbook →"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
